import { Field, Form, Formik } from 'formik'
import React from 'react'

import { Dashboard } from '@components/common/Dashboard/Dashboard'
import { PageLayout } from '@components/common/PageLayout'
import { DashboardBackButton } from '@components/utils'
import {
  Button,
  ButtonClean,
  Fieldset,
  HeadContent,
  Input,
  Subsection,
  Tag,
} from '@fourthwall/components'
import { routing } from '@utils/routing'

import { mapStatusToTagProps } from '../../utils/mapStatusToTagProps'
import type { SettingsFormValues } from './AppsLaylo.types'
import { ConnectValidationSchema } from './consts'
import { useLaylo } from './hooks/useLaylo'

export const AppsLaylo = () => {
  const { laylo } = useLaylo()

  const renderStatusTag = () => {
    const props = mapStatusToTagProps(laylo.status)

    if (!props) return null

    return (
      <div style={{ marginBottom: 20 }}>
        <Tag {...props} size="large" display="inline-flex" />
      </div>
    )
  }

  const renderForm = () => {
    if (laylo.isLoading || laylo.status !== 'NOT_CONNECTED') {
      return null
    }

    return (
      <Subsection title="Connection">
        <Formik<SettingsFormValues>
          initialValues={laylo.connection.initialValues}
          validationSchema={ConnectValidationSchema}
          onSubmit={laylo.connection.onSubmit}
        >
          {({ isSubmitting }) => (
            <Form>
              <div style={{ marginBottom: 16 }}>
                Connect your Laylo account using your API key from the{' '}
                <ButtonClean
                  label="Laylo Integrations Page"
                  href={routing.external.laylo.apiDocs}
                  target="_blank"
                />
                .
              </div>
              <Fieldset>
                <Fieldset.Row>
                  <Fieldset.Item>
                    <Field component={Input} name="apiKey" label="API key" />
                  </Fieldset.Item>
                </Fieldset.Row>
              </Fieldset>
              <div style={{ marginTop: 16 }}>
                <Button
                  type="submit"
                  label="Connect to Laylo"
                  appearance="primary"
                  loading={isSubmitting}
                />
              </div>
            </Form>
          )}
        </Formik>
      </Subsection>
    )
  }

  const renderConnectedContent = () => {
    if (laylo.isLoading || laylo.status === 'NOT_CONNECTED') {
      return null
    }

    return (
      <Subsection title="Connection">
        <div style={{ marginBottom: 16 }}>
          <p style={{ marginBottom: 16 }}>
            Connected to Laylo account.{' '}
            <ButtonClean label="Disconnect" onClick={laylo.onDisconnect} />
          </p>
        </div>
      </Subsection>
    )
  }

  return (
    <>
      <HeadContent title="Laylo" />
      <PageLayout size="tiny">
        <Dashboard.Section
          name="Laylo"
          topContent={<DashboardBackButton label="Apps" to={routing.apps.self} />}
        >
          <Subsection>
            {renderStatusTag()}
            <div>Use Laylo to announce and promote your drops directly to your supporters.</div>
          </Subsection>
          {renderForm()}
          {renderConnectedContent()}
        </Dashboard.Section>
      </PageLayout>
    </>
  )
}
